import { Box, Grid, Heading, Link, Stack, Text } from "@chakra-ui/react";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  bffApi,
  PublicBidStatus,
  useUpdatePublicBidMutation,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
} from "../../../../autogen/bff-api";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
} from "../../../../common/editing/save-coordinator";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { urls } from "../../../../urls";
import { saveWithVersionConflictRetry } from "../../buyer/public/saveWithVersionConflictRetry";
import { PortalLayout } from "../components/PortalLayout";
import { BidWizardNav } from "./BidWizardNav";
import { PreviousSigningBreadcrumb } from "./PreviousSigningBreadcrumb";
import { SigningPill } from "./SigningPill";
import { bidStepNeighbors, useBidStepLabel, type BidStep } from "./bidWizardSteps";
import { BidDocumentsStep } from "./steps/BidDocumentsStep";
import { BidIntroStep } from "./steps/BidIntroStep";
import { ComingStep } from "./steps/ComingStep";
import { CoverLetterStep } from "./steps/CoverLetterStep";
import { ExclusionStep } from "./steps/ExclusionStep";
import { PriceStep } from "./steps/PriceStep";
import { QualificationStep } from "./steps/QualificationStep";
import { ReviewStep } from "./steps/ReviewStep";
import { SupplierInfoStep } from "./steps/SupplierInfoStep";
import { isPublicBidEditable } from "./publicBidEditability";
import { useBidStepNavigation } from "./useBidStepNavigation";

interface Props {
  eventId: string;
  bidId: string;
  bid: PublicBidDto;
  event: PortalSourcingEventDetailDto;
}

export const EditPublicBidForm = ({ eventId, bidId, bid, event }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const [updateBid] = useUpdatePublicBidMutation();
  const collectSupplierPrice = event.collectSupplierPrice ?? true;
  // Two-stage: the ESPD and qualification answers were delivered with the participation request.
  const espdTravelsWithBid = !event.competitionStage;
  const { currentStep, navigateToStep } = useBidStepNavigation(
    eventId,
    bidId,
    collectSupplierPrice,
    espdTravelsWithBid,
  );
  const stepLabel = useBidStepLabel();

  const config = useMemo(
    () => ({
      initialState: bid,
      save: async (fields: Partial<PublicBidPatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateBid({ bidId, publicBidPatchDto: retryFields as PublicBidPatchDto }).unwrap(),
          reloadLatest: async () => {
            await dispatch(bffApi.endpoints.getPublicBid.initiate({ bidId }, { forceRefetch: true })).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [bid, bidId, dispatch, updateBid, editorErrorConfig],
  );

  useCreateEditor<PublicBidDto, PublicBidPatchDto>(bidId, config);

  if (!currentStep) return <PortalLayout>{t("Loading")}</PortalLayout>;

  const goTo = (step: BidStep | undefined): (() => void) | undefined =>
    step
      ? async () => {
          await coordinatorRegistry.flush(bidId);
          if (coordinatorRegistry.get(bidId)?.getEntityStatus() === "error") return;
          navigateToStep(step);
        }
      : undefined;
  const labelFor = (step: BidStep | undefined): string | undefined => (step ? stepLabel(step) : undefined);
  const navPropsFor = (step: BidStep) => {
    const { previous, next } = bidStepNeighbors(step, collectSupplierPrice, espdTravelsWithBid);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: labelFor(next) };
  };

  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case "intro":
        return <BidIntroStep event={event} {...navPropsFor("intro")} />;
      case "supplier":
        return (
          <SupplierInfoStep
            entityId={bidId}
            event={event}
            readOnly={!isPublicBidEditable(bid)}
            {...navPropsFor("supplier")}
          />
        );
      case "exclusion":
        return (
          <ExclusionStep
            entityId={bidId}
            event={event}
            readOnly={!isPublicBidEditable(bid)}
            {...navPropsFor("exclusion")}
          />
        );
      case "qualification":
        return <QualificationStep bidId={bidId} event={event} {...navPropsFor("qualification")} />;
      case "price":
        return <PriceStep bidId={bidId} event={event} {...navPropsFor("price")} />;
      case "documents":
        return <BidDocumentsStep bidId={bidId} {...navPropsFor("documents")} />;
      case "cover":
        return <CoverLetterStep bidId={bidId} {...navPropsFor("cover")} />;
      case "review":
        return (
          <ReviewStep
            eventId={eventId}
            bidId={bidId}
            bid={bid}
            event={event}
            previousStep={navPropsFor("review").previousStep}
          />
        );
      default:
        return <ComingStep title={stepLabel(currentStep)} previousStep={navPropsFor(currentStep).previousStep} />;
    }
  };

  return (
    <PortalLayout>
      <Stack spacing="6">
        <Box>
          <Link as={RouterLink} to={urls.portal.view.go(eventId)} color="smPrimary" fontSize="sm" fontWeight="semibold">
            ← {t("Back to the procurement")}
          </Link>
          <Heading size="lg" mt="2">
            {t("Submit offer")}
          </Heading>
          <Text color="smMuted" fontSize="sm">
            {event.title}
          </Text>
          {bid.signedDocumentId != null && bid.status === PublicBidStatus.Draft && (
            <Box mt="2">
              <SigningPill />
            </Box>
          )}
          {bid.signedDocumentId == null && bid.previousSigningOutcome && (
            <PreviousSigningBreadcrumb outcome={bid.previousSigningOutcome} />
          )}
        </Box>

        <Grid templateColumns={{ base: "1fr", md: "260px 1fr" }} gap={{ base: "4", md: "8" }} alignItems="start">
          <Box display={{ base: "none", md: "block" }} position="sticky" top="6">
            <BidWizardNav
              currentStep={currentStep}
              navigateToStep={navigateToStep}
              collectSupplierPrice={collectSupplierPrice}
              espdTravelsWithBid={espdTravelsWithBid}
            />
          </Box>
          <Box minW="0">{renderStep()}</Box>
        </Grid>
      </Stack>
    </PortalLayout>
  );
};
